import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LocaleLink as Link } from "../../i18n/LocaleLink";
import LanguageSwitcher from "../ui/LanguageSwitcher";

/**
 * SettingsCrown — the watch's setting crown, repurposed as the settings /
 * account / legal access point. A real watch's crown is how you set the time;
 * here it's how you reach account, plans, privacy, terms — everything that
 * isn't part of the 7-chapter reading.
 *
 * Pure UI affordance — pulls right out of the bezel to reveal a small drawer.
 */
export function SettingsCrown() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/account", label: t("ui.web.long_tail.settings.account") },
    { to: "/plans", label: t("ui.web.long_tail.settings.plans") },
    { to: "/saved", label: t("ui.web.long_tail.settings.saved_charts") },
    { to: "/privacy", label: t("ui.web.long_tail.settings.privacy") },
    { to: "/terms", label: t("ui.web.long_tail.settings.terms") },
  ];

  return (
    <div className={`settings-crown ${open ? "is-open" : ""}`}>
      <button
        type="button"
        className="settings-crown__knob"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("ui.web.long_tail.settings.crown_aria")}
        aria-expanded={open}
        title={t("ui.web.long_tail.settings.title")}
      >
        <svg viewBox="0 0 20 20" className="h-full w-full" aria-hidden="true">
          {/* the knurled crown edge */}
          <circle cx="10" cy="10" r="6" fill="none" stroke="var(--b-400)" strokeWidth="1.2" />
          {Array.from({ length: 12 }, (_, i) => {
            const a = ((i * 30 - 90) * Math.PI) / 180;
            return (
              <line
                key={i}
                x1={10 + 6 * Math.cos(a)}
                y1={10 + 6 * Math.sin(a)}
                x2={10 + 7.5 * Math.cos(a)}
                y2={10 + 7.5 * Math.sin(a)}
                stroke="var(--b-400)"
                strokeWidth="0.8"
              />
            );
          })}
          <circle cx="10" cy="10" r="2" fill="var(--b-500)" />
        </svg>
      </button>
      {open && (
        <ul className="settings-crown__drawer">
          {/* Language switcher — i18n 2026-08-27. Lives in the settings
              overflow so the 5-tab shell keeps its footprint; onSelect
              closes the drawer for the navigation it triggers. */}
          <li className="settings-crown__lang px-3 py-2" aria-label={t("ui.web.long_tail.settings.language_aria")}>
            <LanguageSwitcher onSelect={() => setOpen(false)} />
          </li>
          {links.map((l) => (
            <li key={l.to}>
              <Link to={l.to} onClick={() => setOpen(false)}>
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SettingsCrown;
